import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import RoundedModal from '../../../../../../molecules/RoundedModal'
import SydButton from '../../../../../../commonDesign/Button'
import SydSwitch from '../../../../../../commonDesign/SydSwitch'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import { useParseBlueprintFromDql } from '../../../../../../../api/denali'
import { decipherError } from '../../../../../../../utils/decipherError'
import DqlEditor from './DqlEditor'

function mergePurposeData (newPurpose, oldPurpose) {
  const oldColumnMap = new Map(
    (oldPurpose.columns || []).map(col => [col.name, col])
  )

  const mergedColumns = (newPurpose.columns || []).map(col => {
    const old = oldColumnMap.get(col.name)
    if (!old) return col
    return {
      ...col,
      dataType: old.dataType,
      filterable: old.filterable,
      sortable: old.sortable,
      hidden: old.hidden,
      faceted: old.faceted
    }
  })

  return {
    ...newPurpose,
    columns: mergedColumns,
    latch: oldPurpose.latch,
    subordinatePredicates: oldPurpose.subordinatePredicates || [],
    pagination: oldPurpose.pagination
  }
}

function EditPurposeDqlForm ({ purposeCode, purposeData, onUpdate, onClose }) {
  const [dql, setDql] = useState(purposeData?.sourceQuery || '')
  const [preserve, setPreserve] = useState(true)
  const [error, setError] = useState(null)

  const { mutateAsync: parseDql, isLoading: parsing } = useParseBlueprintFromDql()

  const handleParseAndUpdate = useCallback(async () => {
    if (!dql.trim()) return
    setError(null)

    try {
      const result = await parseDql({
        dql: dql.trim(),
        purposeCode,
        displayName: purposeData.displayName
      })

      const newPurpose = result.purposeData
      const finalPurpose = preserve
        ? mergePurposeData(newPurpose, purposeData)
        : newPurpose

      onUpdate(purposeCode, finalPurpose)
      onClose()
    } catch (err) {
      setError(decipherError(err))
    }
  }, [dql, purposeCode, purposeData, preserve, parseDql, onUpdate, onClose])

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: 'var(--color-warning-bg, #fff3cd)',
        border: '1px solid var(--color-warning-border, #ffc107)',
        fontSize: '0.8125rem'
      }}
      >
        Re-parsing resets <strong>columns</strong>, <strong>tables</strong>, and <strong>master predicate</strong> from the new DQL.
        When &ldquo;Preserve compatible settings&rdquo; is on, latch, filter predicates, pagination, and
        column overrides (type, sortable, filterable, hidden, faceted) are carried forward for columns
        whose name matches.
      </div>

      <SydLabel label='DQL Query'>
        <DqlEditor name='edit-purpose-dql' value={dql} onChange={setDql} minLines={6} maxLines={16} />
      </SydLabel>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <SydSwitch
          value={preserve}
          onChange={(e) => setPreserve(e.target.checked)}
        />
        <span style={{ fontSize: '0.8125rem' }}>Preserve compatible settings</span>
      </div>

      {error
        ? (
          <div style={{ color: 'var(--color-danger, red)', fontSize: '0.8125rem' }}>
            {error}
          </div>
        )
        : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <SydButton variant='ghost' onClick={onClose}>Cancel</SydButton>
        <SydButton
          variant='primary'
          onClick={handleParseAndUpdate}
          processing={parsing}
          disabled={!dql.trim()}
        >
          Parse &amp; Update
        </SydButton>
      </div>
    </div>
  )
}

EditPurposeDqlForm.propTypes = {
  purposeCode: PropTypes.string,
  purposeData: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

function EditPurposeDqlModal ({ open, onClose, purposeCode, purposeData, onUpdate }) {
  return (
    <RoundedModal
      title='Edit Source Query'
      open={open}
      onClose={onClose}
      size='large'
      position='top'
      disablePortal={false}
    >
      {open
        ? (
          <EditPurposeDqlForm
            purposeCode={purposeCode}
            purposeData={purposeData}
            onUpdate={onUpdate}
            onClose={onClose}
          />
        )
        : <div />}
    </RoundedModal>
  )
}

EditPurposeDqlModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  purposeCode: PropTypes.string,
  purposeData: PropTypes.object,
  onUpdate: PropTypes.func.isRequired
}

export default EditPurposeDqlModal
